import { useMemo, type ReactNode } from 'react';

import { FontSize, FontWeights } from 'pkg/config/fonts';
import type { Color } from 'pkg/config/palette';
import spacing from 'pkg/config/spacing';
import { palette } from 'pkg/config/styles';

import type * as models from 'pkg/api/models';
import { fullName } from 'pkg/api/models/user/fmt';
import { cssClasses, type CSSPropertiesWithVar } from 'pkg/css/utils';

import Avatar from 'components/avatar';
import MaterialSymbol from 'components/material-symbols';

import Column from 'components/layout/column';
import Row from 'components/layout/row';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';
import {
	TextWrapper,
	type BaseTextWrapperProps,
} from 'components/text/text_wrapper';

import * as css from './styles.css';

/**
 * This component is used as a header in a card. The header can be a headline with an icon,
 * a user with avatar and full name, or custom content. It also supports a secondary
 * headline and a card status.
 * The header can be slim or default size.
 */

interface SubHeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
	headline?: BaseTextWrapperProps;
	headerIcon?: MaterialSymbolVariant;

	user?: models.user.User;
	headerSize?: 'Default' | 'Small';
	secondaryHeadline?: BaseTextWrapperProps;
	cardStatus?: ReactNode;
	slim?: boolean;
	flexFlow?: string;
	color?: Color;
}

export function Header({
	children,

	headline,
	headerIcon,
	headerSize = 'Default',

	secondaryHeadline,

	// displays the headline with an avatar and full name
	user,

	cardStatus,
	className,
	slim,
	flexFlow = 'row wrap',
	color,
	...rest
}: SubHeadingProps) {
	// Memoize header size calculation
	const headerIsDefaultSize = useMemo(
		() => headerSize === 'Default',
		[headerSize]
	);

	const style: CSSPropertiesWithVar = {
		'--flex-flow': flexFlow,
	};

	if (color) {
		style['--backgroundColor'] = palette.getColorSwatch(color)[200];
		style['--textColor'] = palette.getColorSwatch(color)[700];
	}

	const content = () => {
		if (user) {
			return (
				<Row align="center" spacing={spacing._4} columns="auto 1fr">
					<Avatar user={user} size={44} />
					<Column spacing={spacing._1} padding={spacing._0}>
						<TextWrapper
							text={fullName(user)}
							fontSize={FontSize.lg}
							fontWeight={FontWeights.SemiBold}
						/>
						<TextWrapper
							className={css.secondaryHeadline}
							{...secondaryHeadline}
							fontSize={FontSize.sm}
							fontWeight={FontWeights.Normal}
						/>
					</Column>
				</Row>
			);
		}
		if (headline) {
			return (
				<Column spacing={spacing._1} padding={spacing._0}>
					<Row spacing={spacing._2} align="center" columns="auto 1fr">
						{headerIcon && (
							<MaterialSymbol
								variant={headerIcon}
								className={
									headerIsDefaultSize ? css.defaultIcon : css.smallIcon
								}
							/>
						)}
						<TextWrapper
							{...headline}
							fontSize={headerIsDefaultSize ? FontSize.xl : FontSize.lg}
							fontWeight={
								headerIsDefaultSize ? FontWeights.Bold : FontWeights.SemiBold
							}
						/>
					</Row>

					<TextWrapper
						{...secondaryHeadline}
						fontSize={FontSize.base}
						className={css.secondaryHeadline}
					/>
				</Column>
			);
		}

		if (children) {
			return children;
		}

		return null;
	};

	return (
		<header
			className={cssClasses(css.header, slim && css.slim, className)}
			{...rest}
			style={style}>
			{content()}
			{cardStatus && <div className={css.cardStatus}>{cardStatus}</div>}
		</header>
	);
}
